import React, { useState, useCallback, useRef } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { classNames } from "../../utils/utils";

export interface ImageItem {
  src: string;
  alt: string;
}

interface ImagePagerProps {
  images: ImageItem[];
  className?: string;
}

export const ImagePager: React.FC<ImagePagerProps> = ({ images, className = "" }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const previousImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      previousImage();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      nextImage();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < 40) return;
    if (deltaX < 0) nextImage();
    else previousImage();
  };

  return (
    <>
      {/* Desktop Image Grid */}
      <div className={classNames("hidden lg:contents", className)}>
        {images.map((image, index) => (
          <figure key={index} className="group transform hover:-translate-y-2 transition-transform duration-300">
            <div className="rounded-2xl overflow-hidden shadow-md bg-white dark:bg-gray-800">
              <img
                src={process.env.PUBLIC_URL + image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 aspect-[4/3]"
              />
            </div>
          </figure>
        ))}
      </div>

      {/* Mobile Image Pager */}
      <div
        className="relative lg:hidden"
        role="region"
        aria-roledescription="Karussell"
        aria-label="Bildergalerie"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="overflow-hidden rounded-2xl">
          <div className="relative">
            <div
              className="flex motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {images.map((image, index) => (
                <figure key={index} className="w-full flex-shrink-0" aria-hidden={index !== currentImageIndex}>
                  <div className="rounded-2xl overflow-hidden shadow-md bg-white dark:bg-gray-800">
                    <img
                      src={process.env.PUBLIC_URL + image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover aspect-[4/3]"
                    />
                  </div>
                </figure>
              ))}
            </div>

            <div className="absolute inset-0 flex items-center justify-between p-4 pointer-events-none">
              <button
                type="button"
                onClick={previousImage}
                className="pointer-events-auto min-h-11 min-w-11 inline-flex items-center justify-center rounded-full bg-white/80 dark:bg-gray-800/80 text-gray-800 dark:text-white shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all duration-200"
                aria-label="Vorheriges Bild"
              >
                <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={nextImage}
                className="pointer-events-auto min-h-11 min-w-11 inline-flex items-center justify-center rounded-full bg-white/80 dark:bg-gray-800/80 text-gray-800 dark:text-white shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-all duration-200"
                aria-label="Nächstes Bild"
              >
                <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="absolute bottom-4 left-0 right-0">
              <div className="flex justify-center gap-2" aria-label="Bildauswahl">
                {images.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentImageIndex(index)}
                    className="min-h-11 min-w-11 inline-flex items-center justify-center"
                    aria-label={`Bild ${index + 1} von ${images.length}`}
                    aria-current={index === currentImageIndex ? "true" : undefined}
                  >
                    <span
                      className={`block w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                        index === currentImageIndex ? "bg-white scale-125" : "bg-white/50 hover:bg-white/75"
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
